import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Button,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Divider,
  Chip,
  Box,
  MenuItem,
  IconButton,
  Menu,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { DataGrid } from "@mui/x-data-grid";
import { AppContext } from "../context/AppProvider";
import { AuthContext } from "../context/AuthProvider";
import { cloneDeep, debounce } from "lodash";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";
import ConfirmDialog from "../components/ConfirmDialog";
import BundleEditor from "./BundleEditor";
import BundleCreator from "./BundleCreator";

const PAGE_SIZE = 20;
const ROW_HEIGHT = 70;

const STATUS_COLOR = {
  ACTIVE: "success",
  DRAFT: "warning",
  ARCHIVED: "default",
};

const numericId = (gid) => String(gid || "").split("/").pop();

export default function Bundles() {
  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);
  const [formOpen, setFormOpen] = useState(false);
  const [formBundleId, setFormBundleId] = useState(null);

  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [filters, setFilters] = useState({ name: "", status: "" });
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const defaultFilter = { name: "", status: "" };
  const handleReset = () => setFilters(cloneDeep(defaultFilter));

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleMenuOpen = (event, bundle) => {
    setAnchorEl(event.currentTarget);
    setSelectedBundle(bundle);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleEditClick = (bundle) => {
    setFormBundleId(numericId(bundle.id));
    setFormOpen(true);
  };

  const handleCreateClick = () => {
    setFormBundleId(null);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setFormBundleId(null);
    reloadFromStart();
  };

  const handleRemoveClick = () => {
    handleMenuClose();
    setConfirmOpen(true);
  };

  const cursorsRef = useRef([null]);
  const didMountRef = useRef(false);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const handleConfirmDelete = async () => {
    if (!selectedBundle) return;
    try {
      const response = await fetchWithInterceptor(
        `/api/bundles/${numericId(selectedBundle.id)}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
        }
      );
      if (response.ok) {
        appContext.showMessage("Bundle deleted", "success");
        setConfirmOpen(false);
        setSelectedBundle(null);
        reloadFromStart();
      } else {
        const error = await response.json();
        console.error("Failed to delete bundle", error);
        appContext.showMessage(error?.message || "Error deleting bundle", "error");
      }
    } catch (err) {
      console.error("Failed to delete bundle", err);
      appContext.showMessage("Error deleting bundle", "error");
    }
  };

  const fetchPage = async (page) => {
    try {
      setLoading(true);
      const { name, status } = filtersRef.current;
      const params = new URLSearchParams();
      if (name) params.append("name", name);
      if (status) params.append("status", status);
      params.append("limit", String(PAGE_SIZE));
      const cursor = cursorsRef.current[page];
      if (cursor) params.append("cursor", cursor);

      const response = await fetchWithInterceptor(
        `/api/bundles?${params.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch bundles");
      const data = await response.json();
      setBundles(data.rows || []);
      setRowCount(data.total ?? 0);
      cursorsRef.current[page + 1] = data.nextCursor ?? null;
    } catch (err) {
      console.error(err);
      appContext.showMessage("Error loading bundles", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationChange = (model) => {
    setPaginationModel(model);
    fetchPage(model.page);
  };

  const reloadFromStart = () => {
    cursorsRef.current = [null];
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    fetchPage(0);
  };

  const reloadFromFilters = useRef(
    debounce(() => {
      cursorsRef.current = [null];
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      fetchPage(0);
    }, 400)
  ).current;

  // Initial load.
  useEffect(() => {
    fetchPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filters changed: reset to the first page (debounced).
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    reloadFromFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => () => reloadFromFilters.cancel(), [reloadFromFilters]);

  const columns = [
    {
      field: "image",
      headerName: "",
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          {params.row.image ? (
            <Box
              component="img"
              src={params.row.image}
              alt={params.row.title}
              sx={{ width: 52, height: 52, objectFit: "cover", borderRadius: 1 }}
            />
          ) : (
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 1,
                backgroundColor: "rgba(0,0,0,0.06)",
              }}
            />
          )}
        </Box>
      ),
    },
    {
      field: "title",
      headerName: "BUNDLE",
      flex: 1,
      minWidth: 220,
    },
    {
      field: "type",
      headerName: "TYPE",
      width: 220,
      renderCell: (params) => (
        <Chip label={params.row.type?.toUpperCase()} size="small" />
      ),
    },
    {
      field: "status",
      headerName: "STATUS",
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.row.status}
          color={STATUS_COLOR[params.row.status] || "default"}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <>
          <Typography
            variant="body2"
            sx={{
              color: "primary.main",
              cursor: "pointer",
              display: "inline-block",
              mr: 1,
            }}
            onClick={() => handleEditClick(params.row)}
          >
            EDIT
          </Typography>
          <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  if (formOpen) {
    return formBundleId ? (
      <BundleEditor id={formBundleId} onClose={handleCloseForm} />
    ) : (
      <BundleCreator onClose={handleCloseForm} />
    );
  }

  return (
    <>
      <Typography variant="body1" sx={{ ml: 3, mt: 3, mr: 5, mb: 1, color: "text.secondary" }}>
        View and manage product bundles published to the B2C store.
      </Typography>

      <Container sx={{ pb: 3 }} component="main" maxWidth="100%">
        <Paper
          sx={{
            width: "100%",
            backgroundColor: "background.paper",
            mt: 2,
            mb: 2,
          }}
        >
          <div style={{ padding: 16 }}>
            <Typography variant="h6">Search Filters</Typography>
            <br />
            <Grid container spacing={2} alignItems="center" sx={{ px: 0 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  label="Name"
                  name="name"
                  variant="outlined"
                  fullWidth
                  autoComplete="off"
                  value={filters.name}
                  onChange={handleFilterChange}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <TextField
                  select
                  label="Status"
                  name="status"
                  variant="outlined"
                  fullWidth
                  value={filters.status}
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">ALL</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="DRAFT">Draft</MenuItem>
                  <MenuItem value="ARCHIVED">Archived</MenuItem>
                </TextField>
              </Grid>
              <Grid
                size={{ xs: 12, md: "grow" }}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <Button onClick={handleReset}>RESET FILTER</Button>
              </Grid>
            </Grid>
          </div>

          <Divider />

          <Grid container sx={{ px: 4, pb: 2, pt: 2, justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateClick}
            >
              CREATE BUNDLE
            </Button>
          </Grid>

          <DataGrid
            rows={bundles}
            columns={columns}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationChange}
            rowCount={rowCount}
            pageSizeOptions={[PAGE_SIZE]}
            disableRowSelectionOnClick
            disableColumnFilter
            loading={loading}
            getRowId={(row) => row.id}
            rowHeight={ROW_HEIGHT}
            autoHeight
            sx={{
              border: "none",
              "& .MuiDataGrid-menuIcon": { display: "none" },
              "& .MuiDataGrid-columnHeaderTitle": {
                textTransform: "uppercase",
                fontWeight: 600,
                fontSize: "0.75rem",
                color: "rgba(58, 53, 65, 0.87)",
              },
              "& .MuiDataGrid-columnsContainer": {
                backgroundColor: "rgb(249, 250, 252)",
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "none",
              },
            }}
          />
        </Paper>
      </Container>

      <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={handleMenuClose}>
        <MenuItem onClick={handleRemoveClick}>Remove</MenuItem>
      </Menu>

      <ConfirmDialog
        open={confirmOpen}
        title="Delete Bundle"
        message={
          <>
            Are you sure you want to permanently delete
            <strong style={{ fontWeight: 500 }}> "{selectedBundle?.title}"</strong>?
            This action cannot be undone.
          </>
        }
        onCancel={() => {
          setConfirmOpen(false);
          setSelectedBundle(null);
        }}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}
